/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
/* eslint-disable max-lines-per-function */
import { type AfterViewInit, type OnDestroy, type OnInit, Component, Injector, Input, TemplateRef, ViewChild } from '@angular/core';
import { NavigationEnd } from '@angular/router';
import {
    type ColumnDef, type Command, type Rest, type TableConfig, BatchService, ButtonService, DataTableComponent, EventService,
LazyloadService, PromptService, UNPLACEHOLDER,
} from '@dm/core';
import { type Button } from '@dm/core/types';
import { EventServiceKeyE } from 'app/common/constants/event-service-key';
import { RouterActionAbstractComponent } from 'app/common/models/router-action-abstract';
import { SWITCH } from 'app/config';
import { FeatureConfig } from 'app/config/feature';
import { type Subscription } from 'rxjs';

import { type TaskData, Status, TaskCenterService } from './task-center.service';
import { TaskDetailComponent } from './task-detail/task-detail.component';
import { TaskStrService } from './task-str.service';

@Component({
    selector: 'app-task',
    templateUrl: './task.component.html',
    styleUrls: ['./task.component.scss'],
})
export class TaskComponent extends RouterActionAbstractComponent implements OnInit, OnDestroy, AfterViewInit {
    @Input() taskId?: string;
    @ViewChild('dt') table: DataTableComponent;
    @ViewChild('promptTable') promptTable: TemplateRef<any>;

    i18nLabel = this.str.i18nLabel;
    statusText = this.str.statusText;
    icon = this.str.icon;

    dataRest: Rest;
    tableConfig: TableConfig;
    columnDefs: ColumnDef[];

    retryBtn: Button;
    rollbackBtn: Button;
    deleteBtn: Button;

    taskStatus = Status;
    taskNameOpt: { [key: string]: string | number } = {};

    // 选中项
    selectedItems: any[] = [];

    showDetail = false;
    detailData: TaskData;

    deleteTableConfig: TableConfig;
    deleteTableColumnDefs: ColumnDef[];
    promptTableData: TaskData[] = [];

    vstoreSubs$: Subscription[] = [];
    routeChangeSub$: Subscription;
    dataChangeSub$: Subscription;
    // 不支持重试
    retrynotSupport: string[] = [
        'activateSmartContainer',
        'reActivateSmartContainer',
        'activateNasService',
        'switchHMWorkMode',
        'switchHMWorkNodeInAA',
        'switchHMRoleInAP',
        'activateSmartVM',
        'reActivateSmartVM',
    ];

    SWITCH = SWITCH;

    taskDetailComponent;

    get canRollback(): boolean {
        return this.selectedItems.length &&
            this.selectedItems.every((task) => task.taskStatus === Status.failed);
    }

    get canDelete(): boolean {
        return this.selectedItems.length &&
            this.selectedItems.every((task) => task.taskStatus !== Status.running && task.taskStatus !== Status.rollbacking);
    }

    constructor(
        private prompt: PromptService,
        private batch: BatchService,
        private service: TaskCenterService,
        public str: TaskStrService,
        private buttonService: ButtonService,
        private eventService: EventService,
        private lazyloadService: LazyloadService,
        injector: Injector,
    ) {
        super(injector);
    }

    ngOnInit(): void {
        this.listenRouteAction();
        this.initBtns();
        this.initTableConfig();
        this.initDeleteTableConfig();

        // 租户切换处理
        this.vstoreSubs$.push(
            this.layout?.vstoreChange.subscribe(() => {
                // 选择了租户，需要更新rest重新查询
                this.dataRest = this.service.getBatchTaskRest(this.layout.selectedVstore.value);
            }),
        );
    }

    ngAfterViewInit(): void {
        super.ngAfterViewInit();

        this.listenDataChangeSub();
        this.eventService.emit(EventServiceKeyE.TASK_PAGE_LOAD);
    }

    /**
     * 路由改变，触发恢复状态流程
     */
    listenRouteAction(): void {
        this.routeChangeSub$ = this.router.events.subscribe((evt: any) => {
            if (evt instanceof NavigationEnd) {
                this.table.refresh();
            }
        });
    }

    /**
     * 从路由中恢复当前页面状态。当前：任务面板跳转。
     */
    restoreRouteState(): void {
        this.listenDataChangeSub();
    }

    /**
     * 监听数据变化并打开任务详情
     */
    listenDataChangeSub(): void {
        if (this.dataChangeSub$) {
            this.dataChangeSub$.unsubscribe();
        }
        this.dataChangeSub$ = this.table?.onDataChange.subscribe((tableData: TaskData[]) => {
            if (!tableData.length) {
                return;
            }

            if (this.route.snapshot.queryParams.actionParams) {
                this.taskId = this.route.snapshot.queryParams.actionParams;
            }
            this.openTaskDetailPage(tableData, this.taskId);
        });
    }

    openTaskDetailPage(tableData: TaskData[], taskId: string): void {
        if (!tableData) {
            return;
        }
        const task = tableData.find((taskData) => taskData.id === taskId);
        if (task) {
            this.selectedItems = [task];
            this.onShowDetail(task);
        }
    }

    initBtns(): void {
        this.retryBtn = this.buttonService.create({
            id: 'Tasks_retry_btn',
            label: this.i18nLabel.retryBtn,
            permission: ['task_W'],
            disabledCheckWithTips: [
                {
                    tip: '',
                    checker: () => this.selectedItems.length !== 1,
                },
                {
                    tip: this.i18nLabel.retryBtnTip,
                    checker: () => this.selectedItems.length && this.selectedItems.every((task) => task.taskStatus !== Status.failed),
                },
                {
                    tip: this.i18nLabel.retrynotSupportTip,
                    checker: () => this.selectedItems.length && this.selectedItems.every((task) => this.retrynotSupport.includes(task.name)),
                },
            ],
            command: () => {
                this.onRetry();
            },
        });
        this.rollbackBtn = this.buttonService.create({
            id: 'Tasks_Rollback_btn',
            label: this.i18nLabel.rollbackBtn,
            permission: ['task_W'],
            disabledCheck: () => {
                if (this.selectedItems.length === 1) {
                    this.rollbackBtn.disabledTips = this.i18nLabel.rollbackBtnTip;
                } else {
                    this.rollbackBtn.disabledTips = '';
                }
                return !this.canRollback;
            },
            command: () => {
                this.onRollback();
            },
        });
        this.deleteBtn = this.buttonService.create({
            id: 'Tasks_Delete_btn',
            label: this.i18nLabel.deleteBtn,
            permission: ['task_W'],
            disabledCheck: () => {
                if (this.selectedItems.length === 1) {
                    this.deleteBtn.disabledTips = this.i18nLabel.deleteTip;
                } else {
                    this.deleteBtn.disabledTips = '';
                }
                return !this.canDelete;
            },
            command: () => {
                this.onDelete();
            },
        });
    }

    // 初始化表格配置
    initTableConfig(): void {
        const i18nLabel = this.i18nLabel;

        this.dataRest = this.service.getBatchTaskRest();

        this.tableConfig = {
            dataKey: 'id',
            selectionMode: 'checkbox',
            pollingPeriod: 15,
            order: { startTime: 'desc' },
            menuItems: [
                this.buttonService.create({
                    label: this.i18nLabel.retryBtn,
                    permission: ['task_W'],
                    command: (event) => this.onRetry(event.data),
                    disabledCheckWithTips: [
                        {
                            tip: this.i18nLabel.retryBtnTip,
                            checker: (task) => task.taskStatus !== Status.failed,
                        },
                        {
                            tip: this.i18nLabel.retrynotSupportTip,
                            checker: (task) => this.retrynotSupport.includes(task.name),
                        },
                    ],
                }),
                { separator: true },
                this.buttonService.create({
                    label: this.i18nLabel.deleteBtn,
                    disabledTips: this.i18nLabel.deleteTip,
                    permission: ['task_W'],
                    disabledCheck: (task) => task.taskStatus === Status.running || task.taskStatus === Status.rollbacking,
                    command: (event) => this.onDelete(event.data),
                }),
            ],
        };

        this.columnDefs = [
            {
                field: 'name',
                header: i18nLabel.taskName,
                filterType: 'dropdown',
                options: this.taskNameOpt,
                render: (value, col, data: TaskData): string => data.displayName,
                onClick: (data): void => {
                    this.onShowDetail(data);
                },
            },
            {
                field: 'id',
                header: 'ID',
                width: '1rem',
                columnFilter: true,
                hidden: true,
            },
            {
                field: 'objName',
                header: i18nLabel.object,
                filterType: 'search',
                columnFilter: true,
                render: (value) => value || UNPLACEHOLDER,
            },
            {
                field: 'taskStatus',
                header: i18nLabel.taskStatus,
                filterType: 'dropdown',
                options: this.str.taskStatusOptions,
                columnFilter: true,
                minWidth: 120,
                hideTextTips: true,
                render: (value, col, data): string | number => {
                    const { currentStepIndex, stepCount } = data.description || { currentStepIndex: 0, stepCount: 1 };
                    data.progress = Math.floor(currentStepIndex / stepCount * 100);
                    return this.str.taskStatusOptions[value];
                },
            },
            {
                field: 'vstoreName',
                header: i18nLabel.vstoreName,
                columnFilter: true,
                ignore: SWITCH.IS_VSTORE_SYSTEM || !FeatureConfig.IsSupportVStoreService || SWITCH.IS_SIMPLE_MODE,
                render: (value) => value || UNPLACEHOLDER,
            },
            {
                field: 'vstoreId',
                header: i18nLabel.vstoreID,
                width: '1rem',
                columnFilter: true,
                hidden: true,
                ignore: SWITCH.IS_VSTORE_SYSTEM || !FeatureConfig.IsSupportVStoreService || SWITCH.IS_SIMPLE_MODE,
                filterType: 'equalSearch',
                render: (value): string => {
                    // 特殊占位符，非租户id
                    if (value === '4294967295') {
                        return UNPLACEHOLDER;
                    }
                    return value;
                },
            },
            {
                field: 'userSession',
                header: i18nLabel.creator,
                width: '150px',
                filterType: 'search',
                columnFilter: true,
                render: (value, col, data): string => {
                    const creator = value || UNPLACEHOLDER;
                    data.creator = creator;
                    return creator;
                },
            },
            {
                field: 'startTime',
                header: i18nLabel.startTime,
                width: '240px',
                filterType: 'dateTimeRange',
                columnFilter: true,
                sortable: true,
                render: (value) => this.str.renderTime(Number(value)),
            },
            {
                field: 'endTime',
                header: i18nLabel.endTime,
                filterType: 'dateTimeRange',
                columnFilter: true,
                sortable: true,
                render: (value) => this.str.renderTime(Number(value)),
            },
            {
                field: 'duration',
                header: i18nLabel.duration,
                width: '100px',
                columnFilter: true,
                render: (value, col, data): string => {
                    const duration = this.str.getDuration(data.startTime, data.endTime);
                    data.duration = duration;
                    return this.str.renderDuration(duration);
                },
            },
        ];
        this.initTaskTableNameOpt();
    }

    // 初始化删除弹出框的表格配置
    initDeleteTableConfig(): void {
        const i18nLabel = this.i18nLabel;

        this.deleteTableConfig = {
            dataKey: 'id',
        };

        this.deleteTableColumnDefs = [
            {
                header: i18nLabel.taskName,
                field: 'displayName',
            },
            {
                header: i18nLabel.object,
                field: 'objName',
                render: (value) => value || UNPLACEHOLDER,
            },
            {
                header: i18nLabel.taskStatus,
                field: 'taskStatus',
                render: (value) => this.str.taskStatusOptions[value],
            },
            {
                header: i18nLabel.creator,
                field: 'userSession',
                render: (value) => value || UNPLACEHOLDER,
            },
        ];
    }

    initTaskTableNameOpt(): void {
        this.service.queryTaskName().subscribe((results) => {
            const data: { [key: string]: string } = results.data || {};

            if (SWITCH.SUPPORT_NAS) {
                this.taskNameOpt = data;
            } else {
                for (const key of Object.keys(data)) {
                    if (!['startProtectionFilesystem', 'CreateFileResources'].includes(key)) {
                        this.taskNameOpt[key] = data[key];
                    }
                }
            }

            this.table.setFilterOptions('name', this.taskNameOpt);
        });
    }

    onRefresh(): void {
        this.table.refresh();
    }

    onShowDetail(task): void {
        this.showDetail = true;
        this.detailData = { ...task };

        if (SWITCH.IS_SIMPLE_MODE) {
            if (this.taskDetailComponent && this.taskDetailComponent.instance) {
                // 阻止当前侧滑框被关闭
                if (this.taskDetailComponent.instance.slideWindow) {
                    this.taskDetailComponent.instance.slideWindow.isPreventClickHide = true;
                }
                this.taskDetailComponent.instance.task = this.detailData;
                this.taskDetailComponent.instance.updateTaskDetailInfo(this.detailData);
            } else {
                this.taskDetailComponent = this.lazyloadService.appendComponent(TaskDetailComponent, {
                    show: this.showDetail,
                    task: this.detailData,
                    onClose: () => {
                        this.onDetailClose();
                    },
                });
            }
        }

        this.table.stopPolling();
    }

    onDetailClose(): void {
        // 关闭详情页时刷新一下主表格
        this.table.refresh();
        this.table.recoverPolling();
        if (this.dataChangeSub$) {
            this.dataChangeSub$.unsubscribe();
        }
        // 清空路由参数
        super.clearActionParams();

        this.taskDetailComponent = null;
    }

    onRetry(event?): void {
        const tasks = event ? [event] : this.selectedItems;
        const cmds: Command[] = tasks.map((task) => {
            const cmd: Command = {
                name: this.str.retryTaskMsg(task.displayName),
                action: this.service.retryTask(task.id),
            };
            return cmd;
        });

        this.promptTableData = tasks;
        this.table.stopPolling();

        this.prompt.warning({
            id: 'prompt-task-retry',
            width: 700,
            title: this.str.retryTask,
            content: this.str.retryTip,
            templateRef: this.promptTable,
            canHidden: true,
            onOk: () => {
                this.prompt.close();
                this.batch.exePrompt(cmds, {
                    onClose: () => {
                        this.table.refresh();
                        this.table.recoverPolling();
                    },
                });
            },
        });
    }

    onRollback(event?): void {
        const tasks = event ? [event] : this.selectedItems;
        const cmds: Command[] = tasks.map((task) => {
            const cmd: Command = {
                name: this.str.rollbackTaskMsg(task.displayName),
                action: this.service.rollbackTask(task.id),
            };
            return cmd;
        });

        this.promptTableData = tasks;
        this.table.stopPolling();

        this.prompt.promptConfirm({
            width: 700,
            title: this.str.rollbackTask,
            content: this.str.rollbackTip,
            templateRef: this.promptTable,
            onOk: () => {
                this.prompt.close();
                this.batch.exePrompt(cmds, {
                    onClose: () => {
                        this.table.refresh();
                        this.table.recoverPolling();
                    },
                });
            },
        });
    }

    onDelete(event?): void {
        // 如果是极简场景： 删除界面关闭详情划窗
        if (SWITCH.IS_SIMPLE_MODE && this.taskDetailComponent && this.taskDetailComponent.instance) {
            this.taskDetailComponent.instance.show = false;
            this.taskDetailComponent = null;
        }
        const tasks = event ? [event] : this.selectedItems;
        const cmds: Command[] = tasks.map((task) => {
            const cmd: Command = {
                name: this.str.deleteTaskLable(task.displayName),
                action: this.service.deleteTask(task.id),
            };
            return cmd;
        });

        this.promptTableData = tasks;
        this.table.stopPolling();

        this.prompt.promptConfirm({
            width: 700,
            title: this.str.deleteTask,
            content: this.str.deleteTip,
            templateRef: this.promptTable,
            onOk: () => {
                this.prompt.close();
                this.batch.exePrompt(cmds, {
                    onClose: () => {
                        this.table.clearData();
                        this.table.refresh();
                        this.table.recoverPolling();
                    },
                });
            },
        });
    }

    onSelectionChange(selectionList): void {
        if (selectionList && selectionList.length !== 1 && this.showDetail) {
            this.showDetail = false;
        }
    }

    ngOnDestroy(): void {
        this.routeChangeSub$.unsubscribe();
        if (this.dataChangeSub$) {
            this.dataChangeSub$.unsubscribe();
        }

        for (const sub$ of this.vstoreSubs$) {
            sub$?.unsubscribe();
        }

        // 如果是极简场景： 退出界面关闭详情划窗
        if (SWITCH.IS_SIMPLE_MODE && this.taskDetailComponent && this.taskDetailComponent.instance) {
            this.taskDetailComponent.instance.show = false;
            this.taskDetailComponent = null;
        }

        super.ngOnDestroy();
    }
}
